import type {
	TProductDataRegions as ProductDataRegions,
	TProductEdition as ProductEdition,
	TRovoFeatures as RovoFeatures,
} from '@atlassian/search-client';
import type { ExperimentConfig, SideBySideExperiment } from '@atlassian/search-experiment';

import type { NounKeys } from './nouns';
import type { PrimaryProductKey, ProductKeys } from './products';
import type { ThirdPartyConfigsBootstrap } from './schemas/3p-config';
import type { Overrides, SearchQueryStringParams } from './schemas/query-params';
import type { onInputDropdownResultSelect, onInputSubmit, UserDetailsWithPicture } from './types';

/**
 * This state object contains all the configuration which must be loaded to make the page functional.
 */
export const initialState: {
	products: never[];
	nouns: never[];
	allFilterExcludedProducts: never[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	searchSessionId: string;
	queryParams: {};
	overrides?: Overrides;
	prevQueryParams: {};
	initialSpaceARIs: never[];
	experimentConfig: {
		backendExperiment: undefined;
		frontendExperiments: undefined;
		dynamicExperiments: undefined;
		combinedExperiment: undefined;
	};
	experimentLayers: never[];
	isNounsAvailable: false;
	isRovoEnabled: false;
	isConfigLoaded: false;
	rovoFeatures: {};
	isJiraIssueIndexed: false;
	productEdition: undefined;
	productDataRegions: undefined;
	linkedCloudARIs: undefined;
	analyticsAttributes: undefined;
	aggAbsoluteUrl: undefined;
	cloudId: undefined;
	aggregatorHostName: undefined;
	SAINHostName: undefined;
	user: undefined;
	primaryProduct: undefined;
	orgId: undefined;
	intercomHmac: undefined;
	referrer: undefined;
	initialTextQueryParam: undefined;
	features: undefined;
	hasEditOrViewPlanPermission: false;
	onSearchCompleted: undefined;
	smartLinkUsage: undefined;
} = {
	products: [],
	nouns: [],
	allFilterExcludedProducts: [],
	thirdPartyConfigs: {} as ThirdPartyConfigsBootstrap,
	searchSessionId: '',
	queryParams: {},
	prevQueryParams: {},
	overrides: undefined,
	initialSpaceARIs: [],
	experimentConfig: {
		backendExperiment: undefined,
		frontendExperiments: undefined,
		dynamicExperiments: undefined,
		combinedExperiment: undefined,
	},
	experimentLayers: [],
	isNounsAvailable: false,
	isRovoEnabled: false,
	isConfigLoaded: false,
	rovoFeatures: {},
	isJiraIssueIndexed: false,
	productEdition: undefined,
	productDataRegions: undefined,
	linkedCloudARIs: undefined,
	analyticsAttributes: undefined,
	aggAbsoluteUrl: undefined,
	cloudId: undefined,
	aggregatorHostName: undefined,
	SAINHostName: undefined,
	user: undefined,
	primaryProduct: undefined,
	orgId: undefined,
	intercomHmac: undefined,
	referrer: undefined,
	initialTextQueryParam: undefined,
	features: undefined,
	hasEditOrViewPlanPermission: false,
	onSearchCompleted: undefined,
	smartLinkUsage: undefined,
} satisfies BootstrapState;

export type Features = {
	sideBySide?: {
		/**
		 * Whether the side-by-side layout is enabled.
		 */
		enabled: boolean;
		/**
		 * Experiments specifying the treatments to be implemented.
		 */
		experiments?: SideBySideExperiment[];
	};
	chromeExtension?: {
		enabled: boolean;
		onInputDropdownResultSelect: onInputDropdownResultSelect;
		onInputSubmit: onInputSubmit;
	};
};

export type BootstrapState = {
	aggAbsoluteUrl: string | undefined;
	cloudId: string | undefined;
	products: ProductKeys[];
	nouns: NounKeys[];
	aggregatorHostName: string | undefined;
	allFilterExcludedProducts: ProductKeys[];
	SAINHostName: string | undefined;
	user: UserDetailsWithPicture | undefined;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	primaryProduct: PrimaryProductKey | undefined;
	searchSessionId: string;
	orgId: string | undefined;
	analyticsAttributes: Record<string, any> | undefined;
	queryParams: SearchQueryStringParams;
	prevQueryParams: SearchQueryStringParams;
	overrides?: Overrides | undefined;
	/**
	 * The ARIs for the spaces parsed from query params.
	 * This was added to support the query param as a space key
	 * while most backend systems require ARIs to operate.
	 **/
	/**
	 * DO NOT CHANGE FROM [key: type | undefined] to [key?: type]
	 */
	initialSpaceARIs: string[];
	features: Features | undefined;
	experimentConfig: ExperimentConfig;
	experimentLayers: string[];
	intercomHmac: string | undefined;
	isNounsAvailable: boolean;
	isRovoEnabled: boolean;
	isJiraIssueIndexed: boolean;
	isConfigLoaded: boolean;
	referrer: string | undefined;
	productEdition: ProductEdition | undefined;
	productDataRegions: ProductDataRegions | undefined;
	rovoFeatures: RovoFeatures | undefined;
	initialTextQueryParam: string | undefined;
	linkedCloudARIs: string[] | undefined;
	hasEditOrViewPlanPermission?: boolean;
	/** Callback invoked when Quick Find search completes successfully. */
	onSearchCompleted: OnSearchCompletedCallback | undefined;
	/** Smart Link usage data per connector, keyed by product key. Value is usage %. */
	smartLinkUsage: Record<string, number> | undefined;
};

/**
 * Internal type used for the setQueryParams function used within FPS
 */
export type SetQueryParamsCallback = (newParams: Partial<SearchQueryStringParams>) => void;

/**
 * Internal type used for the refreshSearchSessionId function used within QF
 */
export type RefreshSearchSessionIdCallback = () => void;

/**
 * Internal type used for the clearReferrer function used when FPS mounts
 */
export type ClearReferrerCallback = () => void;

/**
 * Internal type used for the setThirdPartyConfigs function used within OAuth workflow
 */
export type SetThirdPartyConfigsCallback = (configs: Partial<ThirdPartyConfigsBootstrap>) => void;

/**
 * Internal type used for the onSearchCompleted callback which is invoked when a search completes successfully, and can be used to trigger side effects such as analytics or refreshing data
 */
export type OnSearchCompletedCallback = (resultCount?: number, query?: string) => void;
